import type { ImageTransform } from "@/types";

import { Area } from "../../../types";

/**
 * REALLY IMPORTANT NOTE:
 * The Image editor work with pixel, but there is all this transformation related to percentages.
 * There is not need for that except that it is only to be compatible with `CroppedImagePreview` and its `calculateClipPath` function!
 * Things are complicated for no reason really!
 */

// This is the REVERSE of extractTransform: given crop percentages, calculate image position/size
export const applyTransform = (
  image: Area,
  clearArea: Area,
  transform: ImageTransform,
) => {
  if (!transform.crop) {
    return {
      x: clearArea.x,
      y: clearArea.y,
      width: clearArea.width,
      height: clearArea.height,
    };
  }
  const { x, y, width, height } = transform.crop;

  const widthRatio = width / image.width;
  const heightRatio = height / image.height;
  const xRatio = x / image.width;
  const yRatio = y / image.height;

  const imageAreaWidth = clearArea.width / widthRatio;
  const imageAreaHeight = clearArea.height / heightRatio;

  const imageX = clearArea.x - xRatio * imageAreaWidth;
  const imageY = clearArea.y - yRatio * imageAreaHeight;
  return {
    x: imageX,
    y: imageY,
    width: imageAreaWidth,
    height: imageAreaHeight,
  };
};

// Convert image area back to transform (reverse of the applyTransform function)
export const extractTransform = (
  image: Area,
  imageArea: Area,
  clearArea: Area,
): ImageTransform => {
  // Calculate the crop bounding box that would produce the given area within the clear area

  // Calculate the crop area that would be visible in the clear area
  // The clear area is at (clearArea.x, clearArea.y) with size (clearArea.width, clearArea.height)
  // The image is at (area.x, area.y) with size (area.width, area.height)

  // The crop area in the image is the intersection of the clear area with the image
  const cropX = clearArea.x - imageArea.x;
  const cropY = clearArea.y - imageArea.y;
  const xRatio = cropX / imageArea.width;
  const yRatio = cropY / imageArea.height;
  const widthRatio = clearArea.width / imageArea.width;
  const heightRatio = clearArea.height / imageArea.height;

  // Convert to pixel coordinates in original image
  const pixelX = xRatio * image.width;
  const pixelY = yRatio * image.height;
  const pixelWidth = widthRatio * image.width;
  const pixelHeight = heightRatio * image.height;

  return {
    crop: {
      x: pixelX,
      y: pixelY,
      width: pixelWidth,
      height: pixelHeight,
    },
  };
};
